/**
 * Bill detail composer-foot — full-width sticky bottom band, mirrors
 * `prototype/purchases/bill-new.html` ▸ `.composer-foot`. Carries the same
 * status-driven primary actions as the header so they remain reachable
 * when the page is scrolled past the top.
 */
import {
  IndianRupee,
  Pencil,
  Printer,
  RotateCcw,
  ShieldCheck,
  Trash2,
  XCircle,
} from "lucide-react";
import { Button } from "@flint/ui";
import { paiseToInrShort } from "../../../lib/format.ts";
import type { BillStatus } from "../list/index.ts";
import { statusLabel } from "./bill-detail-header.tsx";

/** Inputs + callbacks for the bill sticky footer. */
export interface BillDetailFootProps {
  bill: {
    status: BillStatus;
    totalPaise: string;
    vendorName: string;
  };
  isFounder: boolean;
  onEdit: () => void;
  onPrint: () => void;
  onTransition: (status: BillStatus) => void;
  /** Open the record-payment composer. Mirror of the header's prop. */
  onRecordPayment: () => void;
  /** Delete the current draft bill — only rendered when status === 'draft'. */
  onDeleteDraft: () => void;
  pending?: boolean;
}

/** Render the sticky footer mirroring the composer-foot pattern. */
export function BillDetailFoot({
  bill,
  isFounder,
  onEdit,
  onPrint,
  onTransition,
  onRecordPayment,
  onDeleteDraft,
  pending,
}: BillDetailFootProps) {
  return (
    <footer className="composer-foot" aria-label="Detail actions">
      <div className="composer-foot-left">
        <span>{statusLabel(bill.status)}</span>
        <span aria-hidden>·</span>
        <strong>{bill.vendorName}</strong>
        <span aria-hidden>·</span>
        <span className="num">{paiseToInrShort(bill.totalPaise)}</span>
      </div>
      <div className="composer-foot-spacer" />
      <div className="composer-foot-actions">
        <Button type="button" intent="ghost" size="sm" onClick={onPrint}>
          <Printer size={14} />
          Print
        </Button>

        {bill.status === "draft" ? (
          <>
            <Button
              type="button"
              intent="ghost"
              size="sm"
              onClick={onDeleteDraft}
              disabled={pending}
              className="text-bad hover:bg-bad-50"
              title="Delete this draft"
            >
              <Trash2 size={14} />
              Delete draft
            </Button>
            <Button type="button" intent="ghost" size="sm" onClick={onEdit}>
              <Pencil size={14} />
              Edit
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => onTransition("approval_pending")}
              disabled={pending}
            >
              <ShieldCheck size={14} />
              Submit for approval
            </Button>
          </>
        ) : null}

        {bill.status === "review" ? (
          <>
            <Button type="button" intent="ghost" size="sm" onClick={onEdit}>
              <Pencil size={14} />
              Edit
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => onTransition("approval_pending")}
              disabled={pending}
            >
              <ShieldCheck size={14} />
              Submit
            </Button>
          </>
        ) : null}

        {bill.status === "approval_pending" ? (
          <>
            <Button
              type="button"
              intent="ghost"
              size="sm"
              onClick={() => onTransition("review")}
              disabled={pending}
            >
              <RotateCcw size={14} />
              Send back
            </Button>
            <Button
              type="button"
              intent="ghost"
              size="sm"
              onClick={onRecordPayment}
              disabled={pending}
              title="Record a payment if you paid this bill outside Flint before approval"
            >
              <IndianRupee size={14} />
              Record payment
            </Button>
            {isFounder ? (
              <Button
                type="button"
                size="sm"
                onClick={() => onTransition("ready_to_pay")}
                disabled={pending}
              >
                <ShieldCheck size={14} />
                Approve · OTP
              </Button>
            ) : (
              <Button type="button" size="sm" disabled title="Founder OTP required">
                <ShieldCheck size={14} />
                Awaiting founder
              </Button>
            )}
          </>
        ) : null}

        {bill.status === "ready_to_pay" ? (
          <>
            <Button
              type="button"
              intent="ghost"
              size="sm"
              onClick={() => onTransition("void")}
              disabled={pending}
            >
              <XCircle size={14} />
              Void
            </Button>
            <Button type="button" size="sm" onClick={onRecordPayment} disabled={pending}>
              <IndianRupee size={14} />
              Record payment
            </Button>
          </>
        ) : null}
      </div>
    </footer>
  );
}
